// "Discover Syndiqia": each automation in detail (FR first, EN). Long-form copy, kept apart from about-content.ts.
// Keep it true to what the product does: triggers, steps, guardrails and modes as built (see ARCHITECTURE.md §7).

import type { AreaIconName } from '../components/AreaIcon';

/**
 * choice: the board chooses confirmation (default) or automatic for this process.
 * always: runs on its own; it decides nothing (reminders, intake, summaries).
 * confirm: Syndiqia prepares; a person always decides (legal notices, choosing a contractor, minutes).
 */
export type AutomationMode = 'choice' | 'always' | 'confirm';

export interface AutomationDetail {
  icon: AreaIconName;
  title: string;
  mode: AutomationMode;
  /** One sentence: the work it takes off the board. */
  value: string;
  /** How the board does it without Syndiqia. */
  before: string;
  trigger: string;
  steps: string[];
  /** What the board does in confirmation mode (or always, for "confirm"). */
  confirm: string | null;
  /** What happens in automatic mode (choice) or on its own (always). */
  auto: string | null;
  guardrails: string[];
}

export interface AutomationsContent {
  title: string;
  lead: string;
  link: string;
  modes: Record<AutomationMode, string>;
  labels: { before: string; trigger: string; steps: string; confirm: string; auto: string; always: string; guardrails: string };
  items: AutomationDetail[];
  note: string;
}

const fr: AutomationsContent = {
  title: 'Chaque automatisation, en détail',
  lead: 'Ce que Syndiqia fait concrètement, étape par étape : ce qui déclenche le travail, ce que la plateforme prépare ou exécute, ce qui vous reste, et les garde-fous. Chaque processus qui peut agir seul commence en mode confirmation; vous le passez en automatique quand ses propositions sont justes, et vous revenez en arrière en un clic.',
  link: 'Voir chaque automatisation en détail',
  modes: { choice: 'Confirmation ou automatique, au choix', always: 'Automatique : aucune décision en jeu', confirm: 'Préparé par Syndiqia, décidé par vous' },
  labels: { before: 'Sans Syndiqia', trigger: 'Déclencheur', steps: 'Ce que Syndiqia fait', confirm: 'Ce que vous faites', auto: 'En mode automatique', always: 'Sans intervention', guardrails: 'Garde-fous' },
  items: [
    {
      icon: 'requests',
      title: 'Réponses aux demandes et aux questions',
      mode: 'choice',
      value: 'Les questions répétitives (animaux, travaux, stationnement, location) reçoivent une réponse juste, appuyée sur vos documents, sans qu’un administrateur cherche l’article.',
      before: 'Un administrateur lit le courriel, retrouve la déclaration ou le règlement, cherche l’article, rédige la réponse, puis la renvoie le soir ou la fin de semaine.',
      trigger: 'Un copropriétaire écrit une demande dans l’application, ou par courriel à l’adresse du syndicat (demandes@…).',
      steps: [
        'Classe la demande : catégorie (question, bris, plainte, autorisation…), urgence et unité concernée.',
        'Cherche la réponse dans les documents que ce copropriétaire a le droit de lire : déclaration, règlements, procès-verbaux.',
        'Rédige une réponse dans la langue de la personne, qui cite l’article et la page.',
        'Signale ce qui demande une décision du conseil (autorisation, plainte, argent) ou une intervention urgente.',
        'Pour un bris, propose en plus un bon de travail (voir « Bons de travail »).',
      ],
      confirm: 'Le brouillon attend dans la demande : vous le relisez, le modifiez au besoin et l’envoyez en un clic.',
      auto: 'Une réponse sûre part aussitôt, marquée « réponse automatique », et la demande est consignée au journal.',
      guardrails: [
        'Questions d’information seulement, jamais une décision',
        'Réponse appuyée sur un article cité, avec une confiance élevée',
        'Jamais une urgence; jamais si quelqu’un a déjà répondu',
        'Seuls les documents visibles par les membres sont cités, jamais ceux réservés au conseil',
      ],
    },
    {
      icon: 'requests',
      title: 'Courriels reçus classés dans les demandes',
      mode: 'always',
      value: 'Plus de boîte courriel partagée à surveiller : chaque courriel devient une demande suivie, avec ses pièces jointes.',
      before: 'Les courriels arrivent dans la boîte personnelle d’un administrateur, se perdent ou restent sans réponse quand il est absent.',
      trigger: 'Un courriel est envoyé à demandes@<votre-syndicat>.syndiqia.com.',
      steps: [
        'Reconnaît l’expéditeur dans le registre (copropriétaire, occupant).',
        'Crée une demande, ou ajoute la réponse au fil existant quand l’objet mentionne « Demande no N ».',
        'Conserve les photos et les PDF joints avec la demande.',
        'Ignore les réponses automatiques (absence du bureau), les pourriels et les virus, ce qui évite les boucles.',
        'Garde le courriel d’un inconnu avec une note interne pour le conseil, sans jamais lui répondre automatiquement.',
      ],
      confirm: null,
      auto: 'Tout est classé à l’arrivée; la demande suit ensuite le parcours habituel (tri, brouillon de réponse).',
      guardrails: ['Aucune réponse envoyée à une adresse inconnue', 'Un même courriel n’est jamais traité deux fois'],
    },
    {
      icon: 'dues',
      title: 'Relances de paiement',
      mode: 'choice',
      value: 'Les retards sont suivis tous les jours et relancés avec le bon ton, au bon moment, sans qu’un administrateur fasse la comptabilité des arriérés.',
      before: 'Le trésorier compare les dépôts aux cotisations, dresse la liste des retards, écrit à chacun, note les relances et oublie parfois de suivre.',
      trigger: 'Chaque jour, Syndiqia examine le compte de chaque unité.',
      steps: [
        'Première relance quand un montant est en retard de 15 jours, deuxième 15 jours plus tard.',
        'Rédige chaque avis dans la langue du copropriétaire, avec le solde et les échéances en retard.',
        'Retire du lot ceux qui ont payé entre-temps.',
        'Après deux relances, signale le dossier au conseil (mise en demeure, entente ou recours : votre décision).',
        'Envoie avec preuve de remise et consigne chaque étape au dossier de l’unité.',
      ],
      confirm: 'Le lot de relances attend dans la file d’approbation : vous retirez qui vous voulez et approuvez l’envoi.',
      auto: 'Le lot part sans attendre l’approbation, dans les mêmes règles.',
      guardrails: [
        'Jamais tant que les dépôts bancaires récents ne sont pas conciliés (on ne relance pas quelqu’un qui a payé)',
        'Aucune mise en demeure ni poursuite : c’est le conseil qui décide',
      ],
    },
    {
      icon: 'dues',
      title: 'Conciliation bancaire',
      mode: 'choice',
      value: 'Le relevé bancaire se concilie presque seul : chaque dépôt est associé au bon copropriétaire et inscrit aux livres.',
      before: 'Le trésorier pointe ligne par ligne le relevé bancaire contre la liste des cotisations, puis saisit chaque paiement.',
      trigger: 'Vous importez le relevé de la banque (fichier CSV, le format de colonnes est mémorisé).',
      steps: [
        'Écarte les lignes déjà importées.',
        'Associe chaque dépôt à une unité d’après le montant attendu et la description de la transaction.',
        'Propose les correspondances avec un niveau de confiance.',
        'Inscrit le paiement au compte de l’unité et la part de chaque fonds (fonctionnement, prévoyance).',
      ],
      confirm: 'Vous confirmez les correspondances proposées, en un clic pour toutes celles qui sont sûres.',
      auto: 'Les correspondances de confiance élevée sont inscrites dès l’import; les autres attendent.',
      guardrails: ['En automatique, seulement les correspondances de confiance élevée (montant exact et référence reconnue)', 'Une écriture ne se modifie jamais : une erreur se corrige par contrepassation'],
    },
    {
      icon: 'dues',
      title: 'Appels de cotisations',
      mode: 'confirm',
      value: 'Le budget adopté devient les cotisations de chaque unité, réparties entre les fonds, sans tableur.',
      before: 'Le trésorier recalcule dans un tableur la part de chaque unité selon les quotes-parts, sépare le fonds de prévoyance et envoie les montants.',
      trigger: 'Le conseil adopte le budget de l’exercice.',
      steps: [
        'Calcule la cotisation de chaque unité selon les quotes-parts en vigueur (ou à parts égales pour une association).',
        'Sépare la part du fonds de fonctionnement et celle du fonds de prévoyance, comme l’exige la loi.',
        'Prépare les échéances de l’exercice et les inscrit au compte de chaque unité.',
        'Chaque copropriétaire voit son relevé dans son espace.',
      ],
      confirm: 'Vous adoptez le budget, puis inscrivez les échéances en un clic.',
      auto: null,
      guardrails: ['Fonds de fonctionnement et fonds de prévoyance toujours séparés', 'L’adoption du budget reste une décision du conseil'],
    },
    {
      icon: 'maintenance',
      title: 'Bons de travail et envoi aux fournisseurs',
      mode: 'choice',
      value: 'Un bris signalé devient un bon de travail chez le bon fournisseur, qui accepte et propose une date sans un seul appel.',
      before: 'Un administrateur trouve un fournisseur, l’appelle, lui décrit le problème, relance pour avoir une date, puis note où en sont les travaux.',
      trigger: 'Une demande est classée « bris ou entretien ».',
      steps: [
        'Propose un bon de travail : composante touchée, priorité, fournisseur du bon métier choisi dans votre liste.',
        'Rédige le message au fournisseur, sans les renseignements personnels des copropriétaires.',
        'Envoie au fournisseur un lien sécurisé, sans compte : il accepte, propose une date (le travail est planifié), joint des photos et signale la fin.',
        'Les échanges et l’état des travaux sont visibles sur le bon de travail; la fin des travaux s’inscrit au carnet d’entretien.',
      ],
      confirm: 'Vous créez le bon proposé en un clic et envoyez le lien.',
      auto: 'Le bon est créé et le fournisseur reçoit le lien aussitôt, avec le montant que vous autorisez sans soumission; au-delà, il doit soumettre un prix.',
      guardrails: [
        'Travaux non urgents seulement : une urgence reste gérée par une personne',
        'Fournisseur actif, joignable, assurance en vigueur et licence RBQ pour les métiers qui l’exigent',
        'Jamais deux bons pour la même demande; aucun paiement',
      ],
    },
    {
      icon: 'maintenance',
      title: 'Soumissions',
      mode: 'confirm',
      value: 'Obtenir et comparer trois prix ne demande plus une semaine d’appels et de courriels.',
      before: 'Un administrateur appelle plusieurs entrepreneurs, attend leurs prix par courriel, puis les compare dans un tableau en relisant chaque document.',
      trigger: 'Sur un bon de travail sans fournisseur, vous choisissez jusqu’à 5 entrepreneurs à inviter.',
      steps: [
        'Envoie à chacun son propre lien, qui montre seulement la description du travail.',
        'Chacun répond par ce lien : prix avant taxes, délai, validité et sa soumission en PDF, ou décline.',
        'L’agent lit chaque soumission : ce qu’elle couvre, sa validité, ses exclusions.',
        'Compare les réponses dans un tableau, la plus basse en évidence.',
        'Quand vous retenez une soumission : le travail est confié à ce fournisseur avec son prix comme estimation, il reçoit le lien du bon de travail, les autres sont informés.',
      ],
      confirm: 'Vous choisissez qui inviter et quelle soumission retenir.',
      auto: null,
      guardrails: ['Le choix d’un entrepreneur reste toujours le vôtre', 'Un entrepreneur ne voit jamais les autres soumissionnaires'],
    },
    {
      icon: 'maintenance',
      title: 'Factures des fournisseurs',
      mode: 'choice',
      value: 'Les factures arrivent, sont lues, associées au bon de travail et vérifiées : le conseil n’a plus qu’à approuver.',
      before: 'Le trésorier reçoit les factures dans sa boîte courriel, retrouve à quels travaux elles correspondent, vérifie les montants et les taxes, puis les saisit.',
      trigger: 'Le fournisseur envoie sa facture par courriel à factures@<votre-syndicat>.syndiqia.com, comme le fait son logiciel de facturation.',
      steps: [
        'Lit la facture : numéro, date, sous-total, TPS, TVQ, total.',
        'Reconnaît le fournisseur (adresse de l’expéditeur) et le bon de travail (numéro indiqué, ou seul travail en cours de ce fournisseur).',
        'Vérifie : bon fournisseur, travail terminé, montant dans l’estimation (tolérance de 10 %), numéro non répété, TPS et TVQ exactes, total juste.',
        'Ce qui ne peut pas être associé attend dans « Factures à classer », avec des suggestions.',
        'À l’approbation : la dépense et le montant dû au fournisseur sont inscrits aux livres, le coût réel du bon de travail est mis à jour.',
      ],
      confirm: 'Vous voyez la facture, les écarts signalés, et l’approuvez (fonds et compte de dépense au choix) ou la refusez avec un motif.',
      auto: 'Une facture conforme sur tous les points, lue avec confiance et sous le montant que vous fixez, est approuvée seule.',
      guardrails: ['Aucun paiement automatique : payer reste une opération bancaire du conseil', 'Jamais une facture qui présente un écart'],
    },
    {
      icon: 'compliance',
      title: 'Attestations d’assurance des copropriétaires',
      mode: 'choice',
      value: 'Les attestations sont recueillies, lues et suivies unité par unité, avec les rappels avant l’échéance.',
      before: 'Le conseil demande les attestations par courriel, les range dans un dossier, tient un tableau des échéances et relance à la main.',
      trigger: 'Un copropriétaire téléverse son attestation (PDF ou photo), ou le conseil la téléverse pour lui.',
      steps: [
        'Lit l’assureur, le numéro de police, le montant de responsabilité civile et les dates.',
        'Vérifie le montant minimal exigé par le syndicat et la validité.',
        'Suit la couverture unité par unité; rappelle le copropriétaire 30 jours avant l’échéance, puis à l’échéance.',
        'Sur demande du conseil, écrit à ceux qui n’ont rien fourni.',
      ],
      confirm: 'Vous acceptez ou refusez chaque attestation lue.',
      auto: 'Une attestation lue avec confiance, non modifiée et conforme est acceptée seule.',
      guardrails: ['Un refus reste toujours une décision humaine', 'Les attestations sont des renseignements personnels : seuls le conseil et la personne les voient'],
    },
    {
      icon: 'compliance',
      title: 'Calendrier des obligations et carnet d’entretien',
      mode: 'always',
      value: 'Aucune échéance légale, d’assurance ou d’entretien n’est oubliée, et le carnet d’entretien se tient à jour tout seul.',
      before: 'Les échéances vivent dans la mémoire d’un administrateur ou un tableur; le carnet d’entretien est reconstitué quand on en a besoin.',
      trigger: 'Le calendrier standard du Québec est chargé à l’ouverture (obligations de la Loi 16 pour les syndicats), puis complété selon votre immeuble.',
      steps: [
        'Rappelle au conseil chaque obligation 60, 30 et 7 jours avant l’échéance, puis en cas de retard.',
        'Chaque bon de travail terminé et chaque obligation remplie s’inscrit au carnet d’entretien.',
        'Le carnet est inaltérable : une correction s’ajoute, rien ne s’efface.',
      ],
      confirm: null,
      auto: 'Les rappels partent et le carnet s’alimente sans intervention.',
      guardrails: ['Le carnet et l’étude du fonds de prévoyance restent produits par des professionnels; Syndiqia les alimente et les tient à jour'],
    },
    {
      icon: 'governance',
      title: 'Ordre du jour et avis de convocation',
      mode: 'confirm',
      value: 'L’ordre du jour se prépare à partir de ce qui est réellement en suspens, et la convocation part conforme, avec preuve de remise.',
      before: 'Le secrétaire rassemble les sujets dans ses courriels, rédige l’ordre du jour et les résolutions, vérifie le délai légal et envoie la convocation à chacun.',
      trigger: 'Vous créez une assemblée ou une réunion du conseil.',
      steps: [
        'Propose l’ordre du jour type selon le genre d’assemblée et votre structure (syndicat ou association).',
        'Ajoute les sujets en suspens : demandes à trancher, échéances, travaux, suivis des réunions précédentes, avec le libellé des résolutions et la majorité requise.',
        'Prépare l’avis de convocation pour chaque copropriétaire, dans sa langue, et contrôle le délai.',
        'À l’approbation, l’envoie avec preuve de remise conservée.',
      ],
      confirm: 'Vous ajustez l’ordre du jour et approuvez l’avis dans la file d’approbation.',
      auto: null,
      guardrails: ['Un avis légal n’est jamais envoyé sans l’approbation d’un administrateur'],
    },
    {
      icon: 'governance',
      title: 'Déroulement de l’assemblée et procès-verbal',
      mode: 'confirm',
      value: 'Le quorum et les votes se calculent en direct, et le procès-verbal est rédigé pour vous à partir de l’enregistrement.',
      before: 'Le secrétaire calcule le quorum à la main, compte les votes pondérés, prend des notes, puis passe des heures à rédiger le procès-verbal.',
      trigger: 'Pendant la séance : présences, procurations, votes, notes; l’enregistrement audio est facultatif.',
      steps: [
        'Calcule le quorum et le résultat de chaque vote selon les quotes-parts (ou par membre) et la majorité de la résolution.',
        'Transcrit l’enregistrement en français.',
        'Rédige le procès-verbal à partir de l’ordre du jour, des présences, des votes, des notes et de la transcription.',
        'Une fois approuvé, le procès-verbal est publié dans les documents; l’audio est supprimé dès la transcription, la transcription à l’approbation.',
      ],
      confirm: 'Vous relisez et approuvez le procès-verbal.',
      auto: null,
      guardrails: ['Le procès-verbal n’est publié qu’après approbation', 'Les enregistrements ne sont pas conservés'],
    },
    {
      icon: 'reservations',
      title: 'Réservations des installations',
      mode: 'choice',
      value: 'Salle, terrain ou ascenseur de déménagement se réservent seuls, frais compris, sans arbitrage par courriel.',
      before: 'Un administrateur tient un calendrier, répond aux demandes, vérifie les conflits, perçoit les frais et rappelle les règles.',
      trigger: 'Un copropriétaire ou un occupant réserve dans l’application.',
      steps: [
        'Applique vos règles : délai de réservation, quotas par unité, heures, fermetures; un chevauchement est impossible.',
        'Inscrit les frais au compte de l’unité.',
        'Envoie la confirmation, puis un rappel la veille.',
        'Pour les installations qui l’exigent, met la réservation en attente d’approbation.',
      ],
      confirm: 'Vous approuvez ou refusez les réservations qui le demandent.',
      auto: 'Une réservation conforme est approuvée aussitôt si l’unité n’a aucun retard de plus de 30 jours.',
      guardrails: ['Les règles de l’installation s’appliquent toujours', 'Un refus reste une décision du conseil'],
    },
    {
      icon: 'documents',
      title: 'Résumé pour le conseil',
      mode: 'always',
      value: 'Chaque administrateur sait en deux minutes ce qui a été fait seul et ce qui l’attend.',
      before: 'Chacun ouvre plusieurs outils et boîtes courriel pour savoir ce qui a bougé.',
      trigger: 'Chaque matin, ou chaque lundi selon le choix de la personne.',
      steps: [
        'Liste ce que les agents ont fait seuls depuis le dernier résumé.',
        'Liste ce qui attend le conseil : approbations, nouvelles demandes, attestations, transactions à concilier, factures à approuver ou à classer, soumissions à comparer, résolutions à signer.',
      ],
      confirm: null,
      auto: 'Envoyé seulement s’il y a quelque chose à dire.',
      guardrails: ['Chacun choisit : chaque jour, chaque semaine ou jamais'],
    },
  ],
  note: 'Chaque action automatique est consignée dans un journal enchaîné par empreintes, que le conseil peut vérifier. La fiabilité de chaque processus est mesurée (combien de propositions vous confirmez telles quelles) et la plateforme signale quand il est prêt pour le mode automatique.',
};

const en: AutomationsContent = {
  title: 'Every automation, in detail',
  lead: 'What Syndiqia actually does, step by step: what triggers the work, what the platform prepares or carries out, what is left to you, and the guardrails. Every process that can act on its own starts in confirmation mode; you switch it to automatic once its proposals are right, and switch back in one click.',
  link: 'See every automation in detail',
  modes: { choice: 'Confirmation or automatic, your choice', always: 'Automatic: no decision involved', confirm: 'Prepared by Syndiqia, decided by you' },
  labels: { before: 'Without Syndiqia', trigger: 'Trigger', steps: 'What Syndiqia does', confirm: 'What you do', auto: 'In automatic mode', always: 'Without you', guardrails: 'Guardrails' },
  items: [
    {
      icon: 'requests',
      title: 'Answers to requests and questions',
      mode: 'choice',
      value: 'Recurring questions (pets, renovations, parking, rentals) get a correct answer based on your documents, without a director hunting for the article.',
      before: 'A director reads the email, finds the declaration or by-laws, looks for the article, writes the answer, and sends it in the evening or on the weekend.',
      trigger: 'An owner writes a request in the app, or emails the syndicate’s address (demandes@…).',
      steps: [
        'Classifies the request: category (question, repair, complaint, authorization…), urgency and unit.',
        'Looks for the answer in the documents this owner may read: declaration, by-laws, minutes.',
        'Drafts an answer in the person’s language, citing the article and page.',
        'Flags what needs a board decision (authorization, complaint, money) or urgent action.',
        'For a repair, also proposes a work order (see "Work orders").',
      ],
      confirm: 'The draft waits in the request: you read it, edit it if needed and send it in one click.',
      auto: 'A safe answer goes out right away, marked "automatic reply", and is logged.',
      guardrails: [
        'Information questions only, never a decision',
        'Answer backed by a cited article, with high confidence',
        'Never an emergency; never if someone already answered',
        'Only documents visible to members are cited, never board-only ones',
      ],
    },
    {
      icon: 'requests',
      title: 'Emails filed as requests',
      mode: 'always',
      value: 'No shared mailbox to watch: every email becomes a tracked request, with its attachments.',
      before: 'Emails land in a director’s personal mailbox, get lost, or go unanswered when they are away.',
      trigger: 'An email is sent to demandes@<your-syndicate>.syndiqia.com.',
      steps: [
        'Recognizes the sender in the registry (owner, occupant).',
        'Creates a request, or adds the reply to the existing thread when the subject mentions "Demande no N".',
        'Keeps attached photos and PDFs with the request.',
        'Ignores automatic replies (out of office), spam and viruses, which prevents loops.',
        'Keeps an unknown sender’s email with an internal note for the board, never replying automatically.',
      ],
      confirm: null,
      auto: 'Everything is filed on arrival; the request then follows the usual path (triage, draft answer).',
      guardrails: ['No reply is ever sent to an unknown address', 'The same email is never processed twice'],
    },
    {
      icon: 'dues',
      title: 'Payment reminders',
      mode: 'choice',
      value: 'Late payments are watched every day and followed up with the right tone at the right time, without a director tracking arrears.',
      before: 'The treasurer compares deposits with dues, lists who is late, writes to each, notes the reminders, and sometimes forgets to follow up.',
      trigger: 'Every day, Syndiqia reviews each unit’s account.',
      steps: [
        'First reminder when an amount is 15 days late, second 15 days later.',
        'Writes each notice in the owner’s language, with the balance and the late installments.',
        'Removes from the batch anyone who paid in the meantime.',
        'After two reminders, refers the file to the board (formal notice, payment plan or legal steps: your decision).',
        'Sends with proof of delivery and logs each step in the unit’s file.',
      ],
      confirm: 'The reminder batch waits in the approval queue: you remove whoever you want and approve sending.',
      auto: 'The batch goes out without waiting for approval, under the same rules.',
      guardrails: ['Never while recent bank deposits are unreconciled (no reminder to someone who paid)', 'No formal notice or legal action: the board decides'],
    },
    {
      icon: 'dues',
      title: 'Bank reconciliation',
      mode: 'choice',
      value: 'The bank statement reconciles almost by itself: each deposit is matched to the right owner and recorded in the books.',
      before: 'The treasurer ticks the statement line by line against the dues list, then enters each payment.',
      trigger: 'You import the bank statement (CSV file; the column layout is remembered).',
      steps: [
        'Skips lines already imported.',
        'Matches each deposit to a unit from the expected amount and the transaction description.',
        'Proposes matches with a confidence level.',
        'Records the payment on the unit’s account and each fund’s share (operating, contingency).',
      ],
      confirm: 'You confirm the proposed matches, all the sure ones in one click.',
      auto: 'High-confidence matches are recorded at import; the others wait.',
      guardrails: ['In automatic mode, only high-confidence matches (exact amount and recognized reference)', 'An entry is never changed: a mistake is corrected by reversal'],
    },
    {
      icon: 'dues',
      title: 'Dues calls',
      mode: 'confirm',
      value: 'The adopted budget becomes each unit’s dues, split between the funds, without a spreadsheet.',
      before: 'The treasurer recomputes each unit’s share by quote-part in a spreadsheet, separates the contingency fund, and sends the amounts.',
      trigger: 'The board adopts the year’s budget.',
      steps: [
        'Computes each unit’s dues by its current quote-part (or in equal shares for an association).',
        'Separates the operating and contingency fund shares, as the law requires.',
        'Prepares the year’s installments and records them on each unit’s account.',
        'Each owner sees their statement in their space.',
      ],
      confirm: 'You adopt the budget, then post the installments in one click.',
      auto: null,
      guardrails: ['Operating and contingency funds always separate', 'Adopting the budget stays a board decision'],
    },
    {
      icon: 'maintenance',
      title: 'Work orders and dispatch to contractors',
      mode: 'choice',
      value: 'A reported repair becomes a work order with the right contractor, who accepts and proposes a date without a single phone call.',
      before: 'A director finds a contractor, calls them, describes the problem, chases a date, then keeps track of the work.',
      trigger: 'A request is classified as "repair or maintenance".',
      steps: [
        'Proposes a work order: affected component, priority, contractor of the right trade from your list.',
        'Drafts the message to the contractor, without owners’ personal information.',
        'Sends the contractor a secure link, no account: they accept, propose a date (the job is scheduled), attach photos and report it done.',
        'Exchanges and progress show on the work order; completion is written to the maintenance logbook.',
      ],
      confirm: 'You create the proposed work order in one click and send the link.',
      auto: 'The work order is created and the contractor gets the link right away, with the amount you authorize without a quote; above it, they must quote.',
      guardrails: [
        'Non-urgent work only: an emergency stays with a person',
        'Active, reachable contractor with insurance in force and an RBQ licence for trades that require it',
        'Never two work orders for the same request; no payment',
      ],
    },
    {
      icon: 'maintenance',
      title: 'Quotes',
      mode: 'confirm',
      value: 'Getting and comparing three prices no longer takes a week of calls and emails.',
      before: 'A director calls several contractors, waits for prices by email, then compares them in a table, rereading each document.',
      trigger: 'On a work order without a contractor, you pick up to 5 contractors to invite.',
      steps: [
        'Sends each one their own link, showing only the job description.',
        'Each answers through it: price before taxes, timing, validity and their quote as a PDF, or declines.',
        'The agent reads each quote: what it covers, validity, exclusions.',
        'Compares the answers in a table, lowest highlighted.',
        'When you retain a quote: the job goes to that contractor with their price as the estimate, they receive the work order link, the others are told.',
      ],
      confirm: 'You choose whom to invite and which quote to retain.',
      auto: null,
      guardrails: ['Choosing a contractor is always yours', 'A contractor never sees the other bidders'],
    },
    {
      icon: 'maintenance',
      title: 'Contractor invoices',
      mode: 'choice',
      value: 'Invoices arrive, are read, matched to the right job and checked: the board only has to approve.',
      before: 'The treasurer gets invoices in their mailbox, figures out which job each belongs to, checks amounts and taxes, then enters them.',
      trigger: 'The contractor emails the invoice to factures@<your-syndicate>.syndiqia.com, as their billing software does.',
      steps: [
        'Reads the invoice: number, date, subtotal, GST, QST, total.',
        'Recognizes the contractor (sender address) and the work order (number written on it, or that contractor’s only current job).',
        'Checks: right contractor, work completed, amount within the estimate (10% tolerance), number not repeated, correct GST and QST, right total.',
        'Anything that cannot be matched waits in "Invoices to sort", with suggestions.',
        'On approval: the expense and the amount owed are recorded in the books, and the work order’s actual cost is updated.',
      ],
      confirm: 'You see the invoice and any flagged discrepancy, and approve it (fund and expense account of your choice) or reject it with a reason.',
      auto: 'An invoice that passes every check, read with confidence and under the amount you set, is approved on its own.',
      guardrails: ['No automatic payment: paying stays a bank operation by the board', 'Never an invoice with a discrepancy'],
    },
    {
      icon: 'compliance',
      title: 'Owners’ insurance certificates',
      mode: 'choice',
      value: 'Certificates are collected, read and tracked unit by unit, with reminders before they expire.',
      before: 'The board asks for certificates by email, files them, keeps a spreadsheet of expiry dates and chases people by hand.',
      trigger: 'An owner uploads their certificate (PDF or photo), or the board uploads it for them.',
      steps: [
        'Reads the insurer, policy number, civil liability amount and dates.',
        'Checks the minimum amount the syndicate requires and validity.',
        'Tracks coverage unit by unit; reminds the owner 30 days before expiry, then on expiry.',
        'When the board asks, writes to those who have provided nothing.',
      ],
      confirm: 'You accept or reject each certificate read.',
      auto: 'A certificate read with confidence, unchanged and compliant, is accepted on its own.',
      guardrails: ['A rejection is always a human decision', 'Certificates are personal information: only the board and the person see them'],
    },
    {
      icon: 'compliance',
      title: 'Obligations calendar and maintenance logbook',
      mode: 'always',
      value: 'No legal, insurance or maintenance deadline is missed, and the maintenance logbook keeps itself current.',
      before: 'Deadlines live in a director’s memory or a spreadsheet; the logbook is rebuilt when someone needs it.',
      trigger: 'Quebec’s standard calendar is loaded at setup (Bill 16 obligations for syndicates), then completed for your building.',
      steps: [
        'Reminds the board of each obligation 60, 30 and 7 days before it is due, then when overdue.',
        'Every completed work order and fulfilled obligation is written to the maintenance logbook.',
        'The logbook cannot be altered: a correction is added, nothing is erased.',
      ],
      confirm: null,
      auto: 'Reminders go out and the logbook fills itself without you.',
      guardrails: ['The logbook and the contingency fund study are still produced by professionals; Syndiqia feeds them and keeps them current'],
    },
    {
      icon: 'governance',
      title: 'Agenda and notice of meeting',
      mode: 'confirm',
      value: 'The agenda is built from what is actually pending, and the notice goes out compliant, with proof of delivery.',
      before: 'The secretary gathers topics from their emails, writes the agenda and resolutions, checks the legal notice period and sends the notice to everyone.',
      trigger: 'You create an assembly or a board meeting.',
      steps: [
        'Proposes the standard agenda for the kind of meeting and your structure (syndicate or association).',
        'Adds pending items: requests to decide, deadlines, work, follow-ups from earlier meetings, with resolution wording and the required majority.',
        'Prepares the notice for each owner, in their language, and checks the notice period.',
        'On approval, sends it with proof of delivery kept.',
      ],
      confirm: 'You adjust the agenda and approve the notice in the approval queue.',
      auto: null,
      guardrails: ['A legal notice is never sent without a director’s approval'],
    },
    {
      icon: 'governance',
      title: 'Running the meeting and the minutes',
      mode: 'confirm',
      value: 'Quorum and votes are computed live, and the minutes are drafted for you from the recording.',
      before: 'The secretary computes quorum by hand, counts weighted votes, takes notes, then spends hours writing the minutes.',
      trigger: 'During the meeting: attendance, proxies, votes, notes; audio recording is optional.',
      steps: [
        'Computes quorum and each vote’s result by quote-part (or per member) and the resolution’s majority.',
        'Transcribes the recording in French.',
        'Drafts the minutes from the agenda, attendance, votes, notes and transcript.',
        'Once approved, the minutes are published to the documents; audio is deleted once transcribed, the transcript on approval.',
      ],
      confirm: 'You review and approve the minutes.',
      auto: null,
      guardrails: ['Minutes are published only after approval', 'Recordings are not kept'],
    },
    {
      icon: 'reservations',
      title: 'Facility bookings',
      mode: 'choice',
      value: 'Party room, court or move elevator book themselves, fees included, without refereeing by email.',
      before: 'A director keeps a calendar, answers requests, checks conflicts, collects fees and reminds people of the rules.',
      trigger: 'An owner or occupant books in the app.',
      steps: [
        'Applies your rules: booking window, quotas per unit, hours, closures; an overlap is impossible.',
        'Records the fee on the unit’s account.',
        'Sends the confirmation, then a reminder the day before.',
        'For facilities that require it, holds the booking for approval.',
      ],
      confirm: 'You approve or refuse the bookings that need it.',
      auto: 'A booking that meets the rules is approved right away if the unit has nothing more than 30 days overdue.',
      guardrails: ['The facility’s rules always apply', 'A refusal stays a board decision'],
    },
    {
      icon: 'documents',
      title: 'Board summary',
      mode: 'always',
      value: 'Each director knows in two minutes what was done on its own and what is waiting for them.',
      before: 'Everyone opens several tools and mailboxes to find out what changed.',
      trigger: 'Every morning, or every Monday, as each person chooses.',
      steps: [
        'Lists what the agents did on their own since the last summary.',
        'Lists what waits for the board: approvals, new requests, certificates, transactions to reconcile, invoices to approve or sort, quotes to compare, resolutions to sign.',
      ],
      confirm: null,
      auto: 'Sent only when there is something to say.',
      guardrails: ['Each person chooses: daily, weekly or never'],
    },
  ],
  note: 'Every automatic action is recorded in a log chained by fingerprints, which the board can verify. Each process’s accuracy is measured (how many proposals you confirm as they are) and the platform flags when it is ready for automatic mode.',
};

export const AUTOMATIONS: Record<'fr' | 'en', AutomationsContent> = { fr, en };
